import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Trophy } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';
import { UserModal } from '../ui/modals/UserModal';
import { RushHowToPlay } from './RushHowToPlay';

interface RushTopBarProps {
  isLeaderboardOpen?: boolean;
  onLeaderboardToggle?: () => void;
}

export const RushTopBar: React.FC<RushTopBarProps> = ({
  isLeaderboardOpen,
  onLeaderboardToggle,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const layout = useLayoutVariant();
  const [showUserModal, setShowUserModal] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const handleLeaderboardClick = () => {
    if (layout.type === 'desktop' && onLeaderboardToggle) {
      onLeaderboardToggle();
    } else {
      navigate('/trivia-rush/leaderboard');
    }
  };

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top))' }}
      >
        {/* Logo + Brand */}
        <Link to="/" aria-label="Back to Wallstreetle" className="flex items-center gap-2">
          <img
            src="/assets/site-logo/mini-transparent-dark-logo.svg"
            alt="Wallstreetle"
            className="w-7 h-7"
          />
          <span
            className="font-serif text-lg tracking-wide"
            style={{ color: 'hsl(var(--foreground))' }}
          >
            WALLSTREETLE
          </span>
        </Link>

        {/* Right-side buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleLeaderboardClick}
            className="hover:opacity-80 transition-opacity"
            style={{
              color: 'hsl(var(--foreground))',
              textShadow: isLeaderboardOpen ? '0 0 8px hsl(var(--foreground) / 0.6)' : undefined,
            }}
            aria-label="Leaderboard"
          >
            <Trophy className="w-5 h-5" />
          </button>

          <button
            onClick={() => setShowHowToPlay(true)}
            className="text-sm font-bold hover:opacity-80 transition-opacity"
            style={{ color: 'hsl(var(--foreground))' }}
            aria-label="How to play"
          >
            !?
          </button>

          <button
            onClick={() => setShowUserModal(true)}
            className="hover:opacity-80 transition-opacity"
            style={{ color: 'hsl(var(--foreground))' }}
            aria-label={user ? 'Profile' : 'Login'}
          >
            {user ? (
              <User className="w-5 h-5" />
            ) : (
              <span className="text-sm font-medium">Login</span>
            )}
          </button>
        </div>
      </div>

      <RushHowToPlay isOpen={showHowToPlay} onClose={() => setShowHowToPlay(false)} />
      <UserModal isOpen={showUserModal} onClose={() => setShowUserModal(false)} />
    </>
  );
};
